import { useQuery } from '@tanstack/react-query';
import { Dispatch } from 'redux';

import { getFactoringRequestsReceived, requestHandler } from '../../api';
import {
  FactoringRequestToApproveResponse,
  GetFactoringRequestsReceivedResponse,
} from './types';

const MOBILE_PAGE_SIZE = 10;

function buildRequestsQuery(params: {
  page: number;
  pageSize: number;
  statuses?: string[];
}): string {
  let query = '';
  if (params.page) query += `page=${params.page}&`;
  if (params.pageSize) query += `pageSize=${params.pageSize}&`;
  if (params.statuses?.length) {
    query += `statuses=${params.statuses.join(',')}&`;
  }
  return query;
}

export const createFactoringRequest = async (dispatch: Dispatch, data: any) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/factoring/request`,
    data,
    dispatch,
  });
  if (response) return response.data;
};

export const checkFactoringRequestDeletion = async (
  dispatch: Dispatch,
  id: any,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/factoring/request/${id}/checkDelete`,
    dispatch,
  });
  if (response) return response.data;
};

export const deleteFactoringRequest = async (
  dispatch: Dispatch,
  id: number,
) => {
  const response = await requestHandler({
    method: 'delete',
    url: `internal/factoring/request/${id}`,
    dispatch,
  });
  if (response) return response.data;
};

export const updateStatusFactoringRequest = async (
  dispatch: Dispatch,
  id: number,
  data: any,
) => {
  const response = await requestHandler({
    method: 'patch',
    url: `internal/factoring/request/${id}/updateStatus`,
    data,
    dispatch,
  });
  if (response) return response.data;
};

export const editParamsOnReview = async (
  dispatch: Dispatch,
  id: number,
  data: any,
) => {
  const response = await requestHandler({
    method: 'patch',
    url: `internal/factoring/request/${id}/inReview/editParams`,
    data,
    dispatch,
  });
  if (response) return response.data;
};

export const addOwnCompanyConfirmationsToDocuments = async (
  dispatch: Dispatch,
  data: any,
) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/users/factoring/documents/addOwnConfirmation`,
    data,
    dispatch,
  });
  if (response) return response.data;
};

export const addExtraDataToDocument = async (
  dispatch: Dispatch,
  data: {
    documents: any[];
    attributes: string[];
  },
) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/users/factoring/documents/addExtraData`,
    data,
    dispatch,
  });
  if (response) return response.data;
};

export const activateNullFactoringRequest = async (
  dispatch: Dispatch,
  id: number,
  data: any,
) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/factoring/request/${id}/payments/activateNull`,
    data,
    dispatch,
  });
  if (response) return response.data;
};

export const getAvailableArrearTables = async (dispatch: Dispatch) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/factoring/arrearTables`,
    dispatch,
  });
  if (response) return response.data;
};

export const getReplacementInvoices = async (
  dispatch: Dispatch,
  documentId: string,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/factoring/replacementInvoices/${documentId}`,
    dispatch,
  });
  if (response) return response.data;
};

export const replaceInvoice = async (
  dispatch: Dispatch,
  data: any,
  documentId: string,
) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/factoring/replaceInvoice/${documentId}`,
    data,
    dispatch,
  });
  if (response) return response.data;
};

export const getExecutivesAvailableForFactoring = async (
  dispatch: Dispatch,
  businessIdentifier: string,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/users/factoring/executivesAvailable/${businessIdentifier}`,
    dispatch,
  });
  if (response) return response.data;
};

export const editExecutive = async (dispatch: Dispatch, data: any) => {
  const response = await requestHandler({
    method: 'patch',
    url: `internal/users/factoring/executive`,
    data,
    dispatch,
  });
  if (response) return response.data;
};

export const getLastActiveQuote = async (
  dispatch: Dispatch,
  businessIdentifier: string,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/factoring/lastActiveQuote/${businessIdentifier}`,
    dispatch,
  });
  if (response) return response.data;
};

export const getRecieverFactoringRequest = async (
  dispatch: Dispatch,
  data: { requestId: string },
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/users/factoring/reciever/requests/${data.requestId}`,
    dispatch,
  });
  if (response) return response.data;
};

export const getRecieverFactoringRequestOverview = (
  dispatch: Dispatch,
  data: { requestId: string },
) =>
  useQuery({
    queryKey: ['factoring', 'request', data.requestId, 'overview'],
    queryFn: async () => {
      const response = await requestHandler({
        method: 'get',
        url: `internal/users/factoring/reciever/requests/${data.requestId}/overview`,
        dispatch,
      });
      if (response) return response.data;
      return undefined;
    },
    enabled: !!data.requestId,
    staleTime: 1000 * 60 * 5,
    retry: 1,
  });

export const getFactoringRequestsReceivedToApprove = (
  dispatch: Dispatch,
  options?: { enabled?: boolean },
) =>
  useQuery({
    queryKey: ['factoring', 'requestsToApprove'],
    queryFn: async () => {
      const response = await requestHandler({
        method: 'get',
        url: `internal/users/factoring/reciever/requestsToApprove`,
        dispatch,
      });
      if (response) return response.data as FactoringRequestToApproveResponse;
      return { data: [] };
    },
    enabled: options?.enabled ?? true,
    staleTime: 1000 * 60 * 5,
    retry: 1,
  });

const defaultRequestsReceivedResponse: GetFactoringRequestsReceivedResponse = {
  data: [],
  pagination: {
    page: 1,
    pageSize: MOBILE_PAGE_SIZE,
    total: 0,
    totalPages: 0,
  },
};

export const useFactoringRequestsReceived = (
  dispatch: Dispatch,
  params: { page: number; pageSize: number; statuses: string[] },
) =>
  useQuery({
    queryKey: [
      'factoring',
      'requests',
      params.page,
      params.pageSize,
      params.statuses,
    ],
    queryFn: async () => {
      const query = buildRequestsQuery(params);
      const response = await getFactoringRequestsReceived(dispatch, query);
      if (response) return response as GetFactoringRequestsReceivedResponse;
      return defaultRequestsReceivedResponse;
    },
    staleTime: 1000 * 60 * 2,
    retry: 1,
  });
